import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Api, Endpoint } from '../../api/index'
import general from '../../helpers/general'
import styles from './OrderConfirmation.module.scss'
import clsx from 'clsx'

export const POLL_INTERVAL_MS = 30000

const STATUS_STEPS = [
	{ key: 'PENDING', label: 'PLACED', icon: 'fa-shopping-bag' },
	{ key: 'IN_PROGRESS', label: 'COOKING', icon: 'fa-fire' },
	{ key: 'READY', label: 'READY', icon: 'fa-check-circle' },
]

function StatusTracker({ currentStatus }) {
	const currentIndex = STATUS_STEPS.findIndex((step) => step.key === currentStatus)

	return (
		<div className={styles.statusTracker}>
			{STATUS_STEPS.map((step, index) => {
				const isCurrent = index === currentIndex
				const isVisited = index < currentIndex
				const isDisabled = index > currentIndex
				const showConnector = index < STATUS_STEPS.length - 1

				return (
					<React.Fragment key={step.key}>
						<div
							className={clsx(
								styles.statusStep,
								isCurrent && styles.statusStepCurrent,
								isVisited && styles.statusStepVisited,
								isDisabled && styles.statusStepDisabled
							)}
						>
							<div className={styles.statusIcon}>
								<i className={`fas ${step.icon}`}></i>
							</div>
							<div className={styles.statusLabel}>{step.label}</div>
						</div>
						{showConnector && (
							<div
								className={clsx(
									styles.statusConnector,
									index < currentIndex && styles.statusConnectorVisited
								)}
							>
								···
							</div>
						)}
					</React.Fragment>
				)
			})}
		</div>
	)
}

function OrderLineItem({ item }) {
	const lineTotal = (item.unitPrice ?? 0) * (item.quantity ?? 1)
	const variationName = item.variation?.name || item.modifiers?.[0]?.name

	return (
		<li className={styles.lineItem}>
			<div className={styles.lineItemContent}>
				<div className={styles.lineItemDetails}>
					<div className={styles.lineItemName}>
						{item.name} {item.quantity}x
					</div>
					{variationName && <div className={styles.lineItemVariation}>{variationName}</div>}
				</div>
				<div className={styles.lineItemPrice}>{general.formatPrice(lineTotal)}</div>
			</div>
		</li>
	)
}

export default function OrderConfirmation() {
	const { orderId } = useParams()
	const [order, setOrder] = useState(null)
	const [status, setStatus] = useState(null)
	const [liveStatusUnavailable, setLiveStatusUnavailable] = useState(false)

	useEffect(() => {
		let cancelled = false

		async function loadOrder() {
			const response = await Api.call(Endpoint.orders.get, { params: [orderId] })
			if (cancelled || response?.confirmation !== 'Success') return
			setOrder(response.message)
			setStatus(response.message.status)
		}

		loadOrder()
		return () => {
			cancelled = true
		}
	}, [orderId])

	useEffect(() => {
		if (!orderId || !order) return undefined

		let cancelled = false
		let intervalId

		async function pollStatus() {
			const response = await Api.call(Endpoint.orders.status, { params: [orderId, 'status'] })
			if (cancelled || response?.confirmation !== 'Success') return null
			return response.message
		}

		async function refreshStatus() {
			const statusPayload = await pollStatus()
			if (!statusPayload?.status || cancelled) return

			const { status: nextStatus, liveStatusUnavailable: unavailable } = statusPayload
			setStatus((prev) => (prev === nextStatus ? prev : nextStatus))
			if (unavailable) {
				setLiveStatusUnavailable(true)
				if (intervalId) clearInterval(intervalId)
				return
			}
			if ((nextStatus === 'READY' || nextStatus === 'CANCELED') && intervalId) {
				clearInterval(intervalId)
			}
		}

		refreshStatus()
		intervalId = setInterval(refreshStatus, POLL_INTERVAL_MS)

		return () => {
			cancelled = true
			clearInterval(intervalId)
		}
	}, [orderId, order])

	if (!order) {
		return (
			<div className={styles.page}>
				<p>Loading order…</p>
			</div>
		)
	}

	const subtotal = order.lineItems.reduce((sum, item) => sum + (item.unitPrice ?? 0) * (item.quantity ?? 1), 0)
	const tax = (order.total ?? 0) - subtotal

	const isCanceled = status === 'CANCELED'

	return (
		<div className={styles.page}>
			<h1 className={styles.heading}>{isCanceled ? 'Order canceled' : 'Order placed'}</h1>
			<p className={styles.orderId} data-testid="order-id">
				#{orderId}
			</p>

			{isCanceled ? (
				<p className={styles.canceledMessage} data-testid="order-canceled-message">
					This order was canceled and will not be prepared. Please contact the restaurant for more
					information.
				</p>
			) : (
				status && <StatusTracker currentStatus={status} />
			)}
			{liveStatusUnavailable && (
				<p className={styles.statusUnavailableMessage} data-testid="status-unavailable-message">
					We cannot refresh your order status right now. Please check your order status at the restaurant.
				</p>
			)}
			<hr className={styles.divider} />

			<h2 className={styles.sectionHeading}>YOUR ORDER</h2>

			<ul className={styles.itemList}>
				{order.lineItems.map((item, index) => (
					<OrderLineItem key={`${item.name}-${index}`} item={item} />
				))}
			</ul>

			<div className={styles.summary}>
				<div className={styles.summaryRow}>
					<span>Subtotal</span>
					<span>{general.formatPrice(subtotal)}</span>
				</div>
				<div className={styles.summaryRow}>
					<span>Tax</span>
					<span>{general.formatPrice(tax)}</span>
				</div>
				<hr className={styles.divider} />
				<div className={styles.summaryTotal}>
					<span>TOTAL</span>
					<span>{general.formatPrice(order.total)}</span>
				</div>
			</div>
		</div>
	)
}
